import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../cn.js';

type Size = 'sm' | 'md';

export interface SwitchProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'value'
> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  size?: Size;
}

/**
 * A real toggle, not a styled checkbox pretending to be one: `role="switch"` + `aria-checked`
 * (WAI-ARIA switch pattern), on a native `<button>` so Enter/Space activation and focus styling
 * come for free rather than being reimplemented. A label is the caller's job — same division as
 * `Button.tsx`'s bare `children` — pass `aria-label` for an icon-only/labelless switch, or wrap it
 * in a `<label>` (`ReimbursementSettings.tsx`'s checkbox does exactly that for its own control).
 */
const SIZES: Record<Size, { track: string; thumb: string; thumbOn: string }> = {
  sm: { track: 'h-4 w-7', thumb: 'size-3', thumbOn: 'translate-x-3' },
  md: { track: 'h-5 w-9', thumb: 'size-4', thumbOn: 'translate-x-4' },
};

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, size = 'md', disabled, className, ...rest },
  ref,
) {
  const { track, thumb, thumbOn } = SIZES[size];
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full border border-transparent',
        'transition-colors duration-[var(--duration-fast)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-accent' : 'bg-bg-subtle border-border',
        track,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block translate-x-0.5 rounded-full bg-surface shadow-xs',
          'transition-transform duration-[var(--duration-fast)]',
          thumb,
          checked && thumbOn,
        )}
      />
    </button>
  );
});
